import { createRoot, type Root } from "react-dom/client";
import React from 'react';
import App from "./App";
import { AuthProvider } from "../features/auth/contexts/AuthContext";
import { TeamProvider } from "../features/teams/contexts/TeamContext";
import "./index.css";
import "../shared/utils/devUtils"; // Initialize development utilities

// Enhanced error boundary component with recovery
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by boundary:", error);
    console.error("Component stack:", errorInfo.componentStack);
    this.setState({ errorInfo });

    // Auto-retry in development mode after a short delay
    if (import.meta.env.DEV) {
      this.retryTimeoutId = setTimeout(() => {
        console.log("Auto-retrying after error...");
        this.setState({ hasError: false, error: null, errorInfo: null });
      }, 2000);
    }
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "20px", maxWidth: "600px", margin: "50px auto", fontFamily: "system-ui" }}>
          <div style={{ background: "#fee", border: "1px solid #fcc", borderRadius: "8px", padding: "16px" }}>
            <h2 style={{ color: "#c00", margin: "0 0 12px 0" }}>Application Error</h2>
            <p style={{ margin: "0 0 16px 0", color: "#666" }}>
              Something went wrong. This usually resolves automatically in development mode.
            </p>
            <button 
              onClick={this.handleRetry}
              style={{ 
                background: "#007bff", 
                color: "white", 
                border: "none", 
                padding: "8px 16px", 
                borderRadius: "4px", 
                cursor: "pointer",
                marginRight: "8px"
              }}
            >
              Retry
            </button>
            <button 
              onClick={() => window.location.reload()}
              style={{ 
                background: "#6c757d", 
                color: "white", 
                border: "none", 
                padding: "8px 16px", 
                borderRadius: "4px", 
                cursor: "pointer" 
              }}
            >
              Reload Page
            </button>
            {import.meta.env.DEV && this.state.error && (
              <details style={{ marginTop: "16px", whiteSpace: "pre-wrap", fontSize: "12px" }}>
                <summary style={{ cursor: "pointer", color: "#007bff" }}>Error Details</summary>
                <div style={{ background: "#f8f9fa", padding: "12px", marginTop: "8px", borderRadius: "4px" }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Global root instance to prevent duplicate creation
let appRoot: Root | null = null;
const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Failed to find root element");
}

// HMR-aware root creation and management
function renderApp() {
  // Type assertion since we've already checked for null above
  const element = rootElement as HTMLElement;
  
  const appComponent = (
    <ErrorBoundary>
      <AuthProvider>
        <TeamProvider>
          <App />
        </TeamProvider>
      </AuthProvider>
    </ErrorBoundary>
  );

  try {
    // Create root only if it doesn't exist
    if (!appRoot) {
      console.log("Creating new React root");
      appRoot = createRoot(element);
    } else {
      console.log("Reusing existing React root");
    }
    
    appRoot.render(appComponent);
  } catch (error) {
    console.error("Failed to render app:", error);
    
    // Fallback error display
    element.innerHTML = `
      <div style="color: #c00; padding: 20px; font-family: system-ui; max-width: 600px; margin: 50px auto;">
        <h2>Failed to Load Application</h2>
        <p>There was an error initializing the application. Please refresh the page or check the console for details.</p>
        <button onclick="window.location.reload()" style="background: #007bff; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
          Reload Page
        </button>
        ${import.meta.env.DEV ? `<details style="margin-top: 16px;"><summary>Error Details</summary><pre style="background: #f8f9fa; padding: 12px; border-radius: 4px; overflow: auto;">${error}</pre></details>` : ''}
      </div>
    `;
  }
}

// Handle HMR updates in development
if (import.meta.env.DEV && import.meta.hot) {
  import.meta.hot.accept(['./App'], () => {
    console.log("HMR: App updated, re-rendering...");
    renderApp();
  });
  
  // Cleanup on HMR dispose
  import.meta.hot.dispose(() => {
    console.log("HMR: Disposing app...");
    // Don't unmount the root, just let it be reused
  });
}

// Initial render
renderApp();